import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import {
  createComplaint,
  updateComplaint,
  getComplaint,
} from "../../services/complaint.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { readBranches } from "../../services/branch.service.ts";
import { readPositions } from "../../services/position.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import CustomSelectField from "../../components/formik/CustomSelectField.tsx";
import CustomInput from "../../components/formik/CustomInput.tsx";
import CustomTextArea from "../../components/formik/CustomTextArea.tsx";
import moment from "moment";
import "moment/locale/es";
import Button from "../../components/Button.tsx";
moment.locale("es");

const validationSchema = Yup.object().shape({
  name: Yup.string().required("El nombre es requerido"),
  branchId: Yup.number()
    .typeError("La sucursal es requerida")
    .min(1, "La sucursal es requerida")
    .required("La sucursal es requerida"),
  positionId: Yup.number()
    .typeError("El puesto es requerido")
    .min(1, "El puesto es requerido")
    .required("El puesto es requerido"),
  comments: Yup.string().required("Los comentarios son requeridos"),
  date: Yup.string().required("La fecha es requerida"),
});

const NewComplaint = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  useAppSettings();

  const formik = useFormik({
    validationSchema,
    initialValues: {
      name: "",
      branchId: -1,
      positionId: -1,
      comments: "",
      date: moment().format("YYYY-MM-DD"),
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (id) {
          await updateComplaint(Number(id), {
            name: values.name,
            branchId: Number(values.branchId),
            positionId: Number(values.positionId),
            comments: values.comments,
            date: values.date,
          });
          notification("Éxito", "Queja actualizada correctamente", "success");
        } else {
          await createComplaint({
            name: values.name,
            branchId: Number(values.branchId),
            positionId: Number(values.positionId),
            comments: values.comments,
            date: values.date,
          });
          notification("Éxito", "Queja creada correctamente", "success");
        }
        navigate("/quejas");
      } catch (e: any) {
        notification(
          "Error",
          e?.response?.data?.message || "No se pudo guardar la queja",
          "danger"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    readBranches()
      .then((res) => setBranches(res || []))
      .catch(() => setBranches([]));
    readPositions()
      .then((res) => setPositions(res || []))
      .catch(() => setPositions([]));
  }, []);

  useEffect(() => {
    if (id) {
      setLoadingData(true);
      getComplaint(Number(id))
        .then((complaint) => {
          formik.setValues({
            name: complaint.name || "",
            branchId: complaint.branch?.id || "",
            positionId: complaint.position?.id || "",
            comments: complaint.comments || "",
            date: complaint.date
              ? moment(complaint.date).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD"),
          });
        })
        .catch((error) => {
          notification("Error", "No se pudo cargar la queja", "danger");
          navigate("/quejas");
        })
        .finally(() => {
          setLoadingData(false);
        });
    }
  }, [id]);

  return (
    <Layout loading={loadingData}>
      <Title
        baseTitle="Quejas"
        basePath="/quejas"
        title={id ? "Editar queja" : "Nueva queja"}
      />

      <div className="card border-0 m-4">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            <h5 className="card-title mb-0">
              {id ? "Editar queja" : "Nueva queja"}
            </h5>
          </PanelHeader>

          <div className="card-body">
            <CustomInput
              formik={formik}
              field="name"
              label="Nombre"
              placeholder="Nombre de la queja"
              sm={4}
              smLabel={3}
            />

            <CustomSelectField
              formik={formik}
              field="branchId"
              list={branches}
              label="Sucursal"
              placeholder="sucursal"
              sm={4}
              smLabel={3}
            />

            <CustomSelectField
              formik={formik}
              field="positionId"
              list={positions}
              label="Puesto"
              placeholder="puesto"
              sm={4}
              smLabel={3}
            />

            <CustomTextArea
              formik={formik}
              field="comments"
              label="Comentarios"
              placeholder="Escriba los comentarios de la queja"
              sm={4}
              smLabel={3}
            />
            <div className="mt-3" />

            <CustomInput
              formik={formik}
              field="date"
              type="date"
              label="Fecha"
              sm={4}
              smLabel={3}
            />
          </div>
        </Panel>
      </div>
      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate(-1)}
              title="Cancelar"
              variant="secondary"
              type="button"
              disabled={loading}
            />
          </div>
          <div className="col-md-3">
            <Button
              onClick={() => formik.handleSubmit()}
              title={loading ? "Guardando..." : id ? "Actualizar" : "Guardar"}
              variant="azul"
              disabled={loading}
              type="submit"
            />
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default NewComplaint;
